import { useCallback, useMemo } from 'react';
import { canOnFeature } from '@/core/security';
import type { FeatureAction, FeatureGrant } from '@/core/security';
import { useAuth } from '../../common/hooks/useAuth';

export interface FeatureAccess {
  /**
   * Whether the platform actually told us what this role may do.
   *
   * `false` means the question is unanswered — an older platform, a profile fetch that failed, a role
   * with no grants at all — not that the answer is "nothing".
   */
  known: boolean;
  /** The grants as reported, for a caller that needs to show them rather than test one. */
  grants: readonly FeatureGrant[];
  /** Whether to show a control for one action on one feature. */
  can: (feature: string, action: FeatureAction) => boolean;
  /** Whether to show a feature at all: the role holds VIEW on it. */
  canReach: (feature: string) => boolean;
}

/**
 * What the signed-in user's role may do, for deciding what to show.
 *
 * **When the grants are unknown, nothing is hidden.** That is deliberate and it is the safe direction:
 * the client is not a security boundary — the platform refuses a call its grants do not allow, whatever
 * the browser believes — so a client that guessed "deny" on missing data would break a working
 * application for no security gain at all. Guessing "allow" only risks offering a button that answers
 * 403, which is what already happened before any of this existed.
 *
 * Lives in `features/authentication` because it reads the signed-in user. `core/security` holds the predicate and
 * takes the grants as an argument, so it stays free of any dependency on a feature.
 */
export function useFeatureAccess(): FeatureAccess {
  const { user } = useAuth();

  const grants = useMemo<readonly FeatureGrant[]>(
    () => user?.featuresAndPermissions ?? [],
    [user],
  );

  /*
    Whether the platform ANSWERED, not whether the answer was non-empty.

    An empty array is an answer — a role an administrator has created but not yet configured holds
    nothing, and must be shown nothing rather than everything. Only an absent field is unknown, and
    `userMapper` keeps the two apart precisely so this line can tell them apart.
  */
  const known = user?.featuresAndPermissions !== undefined;

  const can = useCallback(
    (feature: string, action: FeatureAction) =>
      !known || canOnFeature(grants, feature, action),
    [grants, known],
  );

  /* VIEW decides visibility -- see `canReachFeature`. */
  const canReach = useCallback((feature: string) => can(feature, 'view'), [can]);

  return { known, grants, can, canReach };
}
